import React from 'react';
import { toast, ToastOptions } from 'react-toastify';

import InformationCircleIcon from '@videoblocks/react-icons/InformationCircle';

import 'react-toastify/dist/ReactToastify.css';

type EditorModalCallback = () => void;

export const SUCCESS_TOAST_ID = 'video-generation-success';
export const PROGRESS_TOAST_ID = 'video-generation-progress';
export const THROTTLED_TOAST_ID = 'video-generation-throttled';
export const ERROR_TOAST_ID = 'video-generation-error';

export function notifySuccess(openVideoEditorModal: EditorModalCallback) {
  toast.dismiss(PROGRESS_TOAST_ID);
  toast.dismiss(ERROR_TOAST_ID);
  toast.dismiss(THROTTLED_TOAST_ID);

  const message = (
    <button className="flex" onClick={openVideoEditorModal}>
      <InformationCircleIcon className="flex-shrink-0 w-5 h-5 text-green-500 fill-current" />
      <div className="pl-5 pr-4 leading-6 text-green-700">
        Your video is ready! Click{' '}
        <span className="font-bold underline">here</span> to view your video
        edit.
      </div>
    </button>
  );

  const defaultOptions: ToastOptions = {
    position: 'bottom-left',
    autoClose: false,
    closeOnClick: true,
    closeButton: false,
    className:
      'cursor-default bg-green-100 border-solid border-green-500 border-t-0 border-r-0 border-b-0 border-l-8 rounded pl-5 pr-8 py-6',
  };

  toast(message, { ...defaultOptions, toastId: SUCCESS_TOAST_ID });
}

export function notifyInProgress(openVideoEditorModal: EditorModalCallback) {
  if (toast.isActive(PROGRESS_TOAST_ID)) {
    return;
  }

  toast.dismiss(SUCCESS_TOAST_ID);
  toast.dismiss(ERROR_TOAST_ID);
  toast.dismiss(THROTTLED_TOAST_ID);
  const message = (
    <button className="flex" onClick={openVideoEditorModal}>
      <InformationCircleIcon className="flex-shrink-0 w-5 h-5 text-gray-500 fill-current" />
      <div className="pl-5 pr-4 leading-6 text-gray-900">
        We&apos;re generating your video, click{' '}
        <span className="font-bold underline">here</span> to check the status.
        This may take a few minutes - we&apos;ll notify you when it&apos;s
        ready.
      </div>
    </button>
  );

  const defaultOptions: ToastOptions = {
    position: 'bottom-left',
    autoClose: false,
    closeOnClick: true,
    className:
      'cursor-default bg-gray-100 border-solid border-gray-500 border-t-0 border-r-0 border-b-0 border-l-8 rounded pl-5 pr-8 py-6',
  };

  toast(message, { ...defaultOptions, toastId: PROGRESS_TOAST_ID });
}

export function notifyThrottled(openVideoEditorModal: EditorModalCallback) {
  if (toast.isActive(THROTTLED_TOAST_ID)) {
    return;
  }

  toast.dismiss(SUCCESS_TOAST_ID);
  toast.dismiss(ERROR_TOAST_ID);
  toast.dismiss(PROGRESS_TOAST_ID);
  const message = (
    <button className="flex" onClick={openVideoEditorModal}>
      <InformationCircleIcon className="flex-shrink-0 w-5 h-5 text-gray-500 fill-current" />
      <div className="pl-5 pr-4 leading-6 text-gray-900">
        Your request has been throttled. Click{' '}
        <span className="font-bold underline">here</span> to check the status.
        This may take a few minutes - we&apos;ll notify you when it&apos;s
        ready.
      </div>
    </button>
  );

  const defaultOptions: ToastOptions = {
    position: 'bottom-left',
    autoClose: false,
    closeOnClick: true,
    className:
      'cursor-default bg-gray-100 border-solid border-gray-500 border-t-0 border-r-0 border-b-0 border-l-8 rounded pl-5 pr-8 py-6',
  };

  toast(message, { ...defaultOptions, toastId: THROTTLED_TOAST_ID });
}

export function notifyError() {
  toast.dismiss(PROGRESS_TOAST_ID);
  toast.dismiss(SUCCESS_TOAST_ID);
  toast.dismiss(THROTTLED_TOAST_ID);
  const message = 'Video generation failed. Please try again later.';

  const defaultOptions: ToastOptions = {
    position: 'bottom-left',
    autoClose: 8000,
    closeOnClick: true,
    className:
      'cursor-default bg-red-100 border-solid border-red-500 border-t-0 border-r-0 border-b-0 border-l-8 rounded pl-5 pr-8 py-6',
  };

  toast.error(message, { ...defaultOptions, toastId: ERROR_TOAST_ID });
}
